"use client";

import { buttonVariants } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { meetingPlatform, type MeetingPlatform } from "../../lib/meeting-url";

const JOINABLE = new Set([
  "scheduled",
  "joining",
  "await_member",
  "await_consent",
  "capturing",
  "paused",
  "consent_hold",
  "member_hold",
]);

const PLATFORM_NAME: Record<MeetingPlatform, string> = {
  zoom: "Zoom",
  meet: "Google Meet",
  teams: "Microsoft Teams",
  webex: "Webex",
};

export function joinableMeetingUrl(
  url: string | null | undefined,
  state?: string | null,
): string | null {
  if (!url || !/^https?:\/\//i.test(url)) return null;
  if (state != null && !JOINABLE.has(state)) return null;
  return url;
}

function PlatformLogo({ platform }: { platform: MeetingPlatform }) {
  if (platform === "zoom") {
    return (
      <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
        <rect width="16" height="16" rx="4" fill="#2D8CFF" />
        <path fill="#fff" d="M3.2 5.4h6.2a.6.6 0 0 1 .6.6v4a.6.6 0 0 1-.6.6H3.2a.6.6 0 0 1-.6-.6v-4a.6.6 0 0 1 .6-.6Z" />
        <path fill="#fff" d="M10.6 7.1 13 5.8v4.4l-2.4-1.3V7.1Z" />
      </svg>
    );
  }
  if (platform === "meet") {
    return (
      <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
        <rect width="16" height="16" rx="4" fill="#fff" />
        <path fill="#00832D" d="M2 6.2h5.2v5.6H3.2A1.2 1.2 0 0 1 2 10.6V6.2Z" />
        <path fill="#0066DA" d="M2 5.4A1.2 1.2 0 0 1 3.2 4.2H7.2v2H2V5.4Z" />
        <path fill="#E94235" d="M7.2 4.2h2.6v7.6H7.2V4.2Z" />
        <path fill="#FFBA00" d="M9.8 6.4 13 4.6v6.8l-3.2-1.8V6.4Z" />
      </svg>
    );
  }
  if (platform === "teams") {
    return (
      <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
        <rect width="16" height="16" rx="4" fill="#6264A7" />
        <circle cx="10.4" cy="4.6" r="1.5" fill="#fff" />
        <path fill="#fff" d="M8.6 6.6h3.6a1 1 0 0 1 1 1V11a2.2 2.2 0 0 1-2.2 2.2h-.2A2.2 2.2 0 0 1 8.6 11V6.6Z" />
        <circle cx="6.2" cy="5.4" r="1.8" fill="#fff" />
        <path fill="#fff" d="M2.4 8.2h4.4A1.2 1.2 0 0 1 8 9.4v2.2A2.6 2.6 0 0 1 5.4 14.2h-.8A2.6 2.6 0 0 1 2 11.6V8.2h.4Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden>
      <rect width="16" height="16" rx="4" fill="#07C1E4" />
      <circle cx="6" cy="8" r="2.2" fill="#fff" />
      <circle cx="10.2" cy="6.2" r="1.5" fill="#fff" />
      <circle cx="10.6" cy="9.8" r="1.2" fill="#fff" />
    </svg>
  );
}

export function JoinMeetingLink({
  url,
  state,
  className,
}: {
  url: string | null | undefined;
  /** When set, the link only shows while the meeting is upcoming or live. */
  state?: string | null;
  className?: string;
}) {
  const href = joinableMeetingUrl(url, state);
  if (!href) return null;
  const platform = meetingPlatform(href);
  if (!platform) return null;
  const tip = `Join on ${PLATFORM_NAME[platform]}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tip}
          onClick={(event) => event.stopPropagation()}
          className={cn(buttonVariants({ variant: "secondary", size: "icon-xs" }), "shrink-0", className)}
        >
          <PlatformLogo platform={platform} />
        </a>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}
